/* eslint-disable */
import type { Coordinates } from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import type { ChangeEventValue } from 'google-map-react';
import type { Logger } from '@feature-hub/logger';
import loggerStateContext from '../../../contexts/logger-context';
import mapContext from '../../../contexts/map-context';
import type { Action } from '../../../contexts/reducers/map-reducer';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import { useIsMobileScreen, useWindowSize } from '../../../hooks/useWindowSize';
import { useSetSearchInThisAreaVisible } from './Map.hooks';
import {
  calculateZoomLevelWithGeocoderResult,
  findFirstCountry,
} from '../../../utils/geocoderResultProcessor';
import type { WindowSize } from '../../../models/common/windowSize';
import { CountryNames } from '../../../models/common/places';

type GoogleMapApi = {
  map: {
    setCenter: (coordinate: Coordinates) => void;
    setZoom: (zoom: number) => void;
  };
};

export const handleMapDragEnd = () => {
  const setSearchInThisAreaVisible = useSetSearchInThisAreaVisible();

  return () => {
    setSearchInThisAreaVisible(true);
  };
};

export const handleMapDrag = () => {
  const dispatch = mapContext.useMapDispatch();

  return () => {
    dispatch({ type: ActionTypes.setSearchInThisAreaVisible, payload: false });
    dispatch({ type: ActionTypes.setIsUserPositionUsed, payload: false });
    dispatch({ type: ActionTypes.resetGeolocationSearch });
  };
};

export const handleMapChange = () => {
  const dispatch = mapContext.useMapDispatch();

  return (viewport: ChangeEventValue) => {
    dispatch({ type: ActionTypes.setMapZoom, payload: viewport.zoom });
    dispatch({ type: ActionTypes.setBounds, payload: viewport.bounds });
  };
};

export const handleMapClick = () => {
  const isMobileScreen = useIsMobileScreen();
  const { selectedDealerId, isAutocompletionOpen } = mapContext.useMapState();
  const dispatch = mapContext.useMapDispatch();

  return () => {
    if (isMobileScreen && selectedDealerId) {
      dispatch({
        type: ActionTypes.setSelectedDealerId,
        payload: null,
      });
      dispatch({
        type: ActionTypes.setDealerReviews,
        payload: [],
      });
      dispatch({
        type: ActionTypes.setRatingSummary,
        payload: null,
      });
    }
    if (isAutocompletionOpen) {
      dispatch({
        type: ActionTypes.setIsAutocompletionOpen,
        payload: false,
      });
    }
  };
};

const handleRegion = async (
  country: string,
  dispatch: React.Dispatch<Action>,
  windowSize: WindowSize,
  logger: Logger,
  initialMapZoomLevel: string,
): Promise<void> => {
  try {
    const result: google.maps.GeocoderResult | undefined = await findFirstCountry(country);
    if (result) {
      dispatch({
        type: ActionTypes.setMapCenterCoordinates,
        payload: result.geometry.location.toJSON(),
      });
      let zoomLevel;
      if (initialMapZoomLevel) {
        dispatch({ type: ActionTypes.setMapZoom, payload: Number(initialMapZoomLevel) });
      } else {
        zoomLevel = calculateZoomLevelWithGeocoderResult(result, windowSize);
        dispatch({ type: ActionTypes.setMapZoom, payload: zoomLevel });
      }
    }
  } catch (err) {
    logger.error('An error occured', err);
  }
};

export const handleGoogleApiLoad = () => {
  const {
    mapZoom,
    mapCenterCoordinates,
    query,
    searchGeoLocation,
    locale,
    initialMapZoomLevel,
    isGoogleMapsApiLoaded,
  } = mapContext.useMapState();
  const { logger } = loggerStateContext.useLoggerState();
  const dispatch = mapContext.useMapDispatch();
  const windowSize = useWindowSize();

  if (isGoogleMapsApiLoaded) {
    return ({ map: _map }: GoogleMapApi) => {
      dispatch({ type: ActionTypes.setIsGoogleMapsApiLoaded, payload: true });
    };
  }

  return ({ map }: GoogleMapApi) => {
    if (!map) {
      return;
    }

    map.setCenter(mapCenterCoordinates);
    map.setZoom(mapZoom);

    logger.debug('Google Maps API is loaded');
    dispatch({ type: ActionTypes.setIsGoogleMapsApiLoaded, payload: true });

    if (!query.length && searchGeoLocation.lat === 0 && searchGeoLocation.lng === 0) {
      const country = CountryNames[locale.countryCode as keyof typeof CountryNames];

      void handleRegion(country, dispatch, windowSize, logger, initialMapZoomLevel);
    }
  };
};
